import './insured-entity.detail.overview.scss';
import './../common.scss';

import {
  AliasType,
  ContactType,
  Entity,
  FundamentalEdges,
  MidtermTransactionEdges,
  Provider,
  SubmissionDetailInsuredEntityAddressData,
  SubmissionDetailInsuredEntityAliasData,
  SubmissionDetailInsuredEntityDomainData,
  SubmissionDetailInsuredEntityFundamentalsData,
  SubmissionDetailInsuredEntityIndustrySectorData,
  getPaginatedNodes,
  sortSubmissionDetailsBySource,
} from '@converge/graphql';
import { CreateClaim, Option } from '../../claim/create-claim/create.claim';
import {
  DialogLayout,
  ErrorPageLayout,
  PaperLayout,
} from '../../../../presentation';
import { FormProvider, useForm } from 'react-hook-form';
import { useMemo, useState } from 'react';

import { DialogTitle } from '@mui/material';
import { InsuredEntityPageContainer } from '../insured-entity.page.container';
import { InsuredEntityPolicyHolderInfoFormSection } from './form-sections';
import { convergeLogger } from '@converge/config';
import { toTitleCase } from '@converge/data';
import { useOutletContext } from 'react-router-dom';

type Nullable<T> = T | null | undefined;

interface CreateClaimModalProps {
  onClose?: () => void;
  insuredUuid: string;
  title: string;
  subTitle?: string;
}

type ActiveExpiredPoliciesProps = {
  activePolicies: PolicyDetailProps[];
  expiredPolicies: PolicyDetailProps[];
};

export type PolicyDetailProps = {
  uuid?: string;
  updatedAt?: string;
  referenceId?: string | null;
  type?: string | null;
  netPremium?: number | null;
  startDate?: string;
  endDate?: string;
  limit?: number | null;
  retention?: number | null;
  status?: string | null;
  documentLink?: string | null;
  isActive?: boolean | null;
  grossPremium?: number;
  showCancellationModal?: boolean | null;
  midtermTransactions: MidtermTransactionEdges;
  brokerName?: string | null;
  brokerUuid?: string | null | undefined;
};

export const CreateClaimModal = ({
  onClose,
  title,
  subTitle,
  insuredUuid,
}: CreateClaimModalProps) => {
  return (
    <DialogLayout
      open
      onClose={onClose}
      showCloseButton={true}
      onCloseIconClick={onClose}
      header={
        <DialogTitle variant="h5" color="secondary" sx={{ m: 0, p: 0 }}>
          {title}{' '}
        </DialogTitle>
      }
    >
      <CreateClaim insuredUuid={insuredUuid} onClose={onClose} />
    </DialogLayout>
  );
};

interface IEntityAddress {
  uuid: Nullable<string>;
  street1: Nullable<string>;
  street2: Nullable<string>;
  city: Nullable<string>;
  timezone: Nullable<string>;
  region: Nullable<string>;
  regionName: Nullable<string>;
  regionCode: Nullable<string>;
  postalCode: Nullable<string>;
  country: Nullable<string>;
  countryName: Nullable<string>;
  source: Nullable<string>;
  iso2Code: Nullable<string>;
}

export interface IEntityOverviewFormData {
  insuredEntity: {
    uuid: Nullable<string>;
    referenceId: Nullable<string>;
    companyType: Nullable<string>;
    primaryLegalAlias: Nullable<SubmissionDetailInsuredEntityAliasData>;
    primaryAddress: Nullable<IEntityAddress>;
    primaryFundamentals: Nullable<SubmissionDetailInsuredEntityFundamentalsData>;
    primaryIndustrySector: Nullable<SubmissionDetailInsuredEntityIndustrySectorData>;
    tradingNames: Nullable<SubmissionDetailInsuredEntityAliasData[]>;
    domains: Nullable<SubmissionDetailInsuredEntityDomainData[]>;
    otherAddresses: Nullable<IEntityAddress[]>;
    otherIndustrySectors: Nullable<
      SubmissionDetailInsuredEntityIndustrySectorData[]
    >;
    primaryTradingName: Nullable<SubmissionDetailInsuredEntityAliasData>;
    otherLegalAliases: Nullable<SubmissionDetailInsuredEntityAliasData[]>;
    otherFundamentals: Nullable<
      SubmissionDetailInsuredEntityFundamentalsData[]
    >;
    primaryDomain: Nullable<string>;
    documentLink: Nullable<string>;
    primaryContact: {
      uuid: Nullable<string>;
      firstName: Nullable<string>;
      lastName: Nullable<string>;
      email: Nullable<string>;
      phone: Nullable<string>;
    };
  };
}
export interface IInsuredOutletProps {
  insuredUuid: string;
}
export interface IInsuredEntityPagination {
  currentPage: number;
  setCurrentPage: (current: number) => void;
  rowsPerPage: number;
  setRowsPerPage: (size: number) => void;
}
export interface InsuredEntityProps {
  insuredEntity: Entity;
  policiesOptions: Option[];
  policiesList: ActiveExpiredPoliciesProps;
}
export const usePagination = () => {
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  return {
    currentPage,
    setCurrentPage,
    rowsPerPage,
    setRowsPerPage,
  };
};

export const InsuredEntityDetailOverview = () => {
  const { insuredEntity } = useOutletContext<InsuredEntityProps>();
  if (!insuredEntity) {
    return (
      <ErrorPageLayout
        message="No insured data found"
        messageId="insured.detail.page.insuredEntityNotFound"
      />
    );
  }

  return <InsuredEntityDetailOverviewInner data={insuredEntity} />;
};

export const InsuredEntityDetailOverviewInner = ({
  data,
}: {
  data: InsuredEntity;
}) => {
  const defaultValues = useMemo(() => mapToUIOverviewModel(data), [data]);

  const formMethods = useForm({
    defaultValues,
  });

  return (
    <InsuredEntityPageContainer title="Overview">
      <PaperLayout className="insured-entity-page-layout">
        <FormProvider {...formMethods}>
          <div className="form-wrapper">
            <InsuredEntityPolicyHolderInfoFormSection isReadonly={true} />
          </div>
        </FormProvider>
      </PaperLayout>
    </InsuredEntityPageContainer>
  );
};

export const mapInsuredEntityToFormData = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  insuredEntityData?: InsuredEntity,
  policyFundamentals?: FundamentalEdges['edges'] | null
): IEntityOverviewFormData['insuredEntity'] => {
  const primaryAddress = insuredEntityData?.addresses?.edges?.find((address) =>
    isPrimary(address.node)
  )?.node;

  const primaryDomains = getPaginatedNodes(insuredEntityData?.domains).filter(
    (domain) => isPrimary(domain)
  );
  const nonPrimaryDomains = getPaginatedNodes(
    insuredEntityData?.domains
  ).filter((domain) => !isPrimary(domain));

  const sortedDomains = [...primaryDomains, ...nonPrimaryDomains];

  const insuredContact =
    getPaginatedNodes(insuredEntityData?.persons)?.find(
      (person) => person.source === Provider.Primary
    ) ??
    getPaginatedNodes(insuredEntityData?.persons)?.find(
      (person) => person.source === Provider.Converge
    );

  return {
    uuid: insuredEntityData?.uuid,
    documentLink: insuredEntityData?.documentLink,
    referenceId: insuredEntityData?.referenceId,
    companyType: toTitleCase(insuredEntityData?.type ?? ''),
    primaryDomain: primaryDomains[0]?.domain,
    domains: sortSubmissionDetailsBySource(sortedDomains),

    primaryAddress: mapAddressToUIModel(primaryAddress),
    otherAddresses:
      sortSubmissionDetailsBySource(
        getPaginatedNodes(insuredEntityData?.addresses).filter(
          (address) => !isPrimary(address)
        )
      )?.map((address) => mapAddressToUIModel(address)) || [],
    primaryIndustrySector: getPaginatedNodes(
      insuredEntityData?.industrySectors
    ).find((industrySector) => isPrimary(industrySector)),
    otherIndustrySectors: sortSubmissionDetailsBySource(
      getPaginatedNodes(insuredEntityData?.industrySectors)?.filter(
        (industrySector) =>
          industrySector.sources?.length === 1
            ? industrySector.sources[0] !== Provider.Primary
            : true
      )
    ),
    primaryTradingName: getPaginatedNodes(insuredEntityData?.aliases).find(
      (legalName) =>
        isPrimary(legalName) && legalName.type === AliasType.TradingName
    ),
    tradingNames: sortSubmissionDetailsBySource(
      getPaginatedNodes(insuredEntityData?.aliases)?.filter(
        (data) =>
          data.type === AliasType.TradingName &&
          !isPrimary(data) &&
          data.name &&
          data.name !== ''
      )
    ),
    primaryLegalAlias: getPaginatedNodes(insuredEntityData?.aliases).find(
      (alias) => isPrimary(alias) && alias.type === AliasType.LegalName
    ),
    otherLegalAliases: sortSubmissionDetailsBySource(
      getPaginatedNodes(insuredEntityData?.aliases)?.filter(
        (alias) =>
          alias.type === AliasType.LegalName &&
          (alias.sources?.length === 1
            ? alias.sources[0] !== Provider.Primary
            : true)
      )
    ),
    primaryFundamentals: policyFundamentals
      ? policyFundamentals.find(
          (fundamentalNode) => fundamentalNode.node.primary
        )?.node
      : insuredEntityData?.fundamentals
      ? getPaginatedNodes(insuredEntityData?.fundamentals)?.find(
          (fundamental) => isPrimary(fundamental)
        )
      : null,
    otherFundamentals: sortSubmissionDetailsBySource(
      getPaginatedNodes(insuredEntityData?.fundamentals)?.filter(
        (fundamental) =>
          fundamental.sources?.length === 1
            ? fundamental.sources[0] !== Provider.Primary
            : true
      )
    ),
    primaryContact: {
      uuid: insuredContact?.uuid,
      firstName: insuredContact?.firstName,
      lastName: insuredContact?.lastName,
      email: getPaginatedNodes(insuredContact?.contacts)?.find(
        (contact) => contact.type === ContactType.Email
      )?.value,
      phone: getPaginatedNodes(insuredContact?.contacts)?.find(
        (contact) => contact.type === ContactType.Phone
      )?.value,
    },
  };
};

type InsuredEntity = Omit<Entity, 'coverages' | 'ipAddresses' | 'softwares'>;

export const mapToUIOverviewModel = (
  data: InsuredEntity
): IEntityOverviewFormData => {
  const insuredEntity = mapInsuredEntityToFormData(data);

  const defaultValues: IEntityOverviewFormData = {
    insuredEntity: insuredEntity,
  };

  convergeLogger.debug('defaultValues', defaultValues);

  return defaultValues;
};

export const mapAddressToUIModel = (
  apiAddress: SubmissionDetailInsuredEntityAddressData | undefined
): IEntityAddress => {
  return {
    uuid: apiAddress?.uuid,
    street1: apiAddress?.street1,
    street2: apiAddress?.street2,
    city: apiAddress?.city,
    timezone: apiAddress?.timezone,
    region: apiAddress?.regions?.edges[0]?.node.uuid,
    regionName: apiAddress?.regions?.edges[0]?.node.name,
    regionCode: apiAddress?.regions?.edges[0]?.node.iso2Code,
    postalCode: apiAddress?.postalCode,
    country: apiAddress?.countries?.edges[0]?.node.uuid,
    countryName: apiAddress?.countries?.edges[0]?.node?.name,
    source: apiAddress?.source,
    iso2Code: apiAddress?.countries?.edges[0]?.node?.iso2Code,
  };
};

export const isPrimary = ({
  primary,
  sources,
  source,
}: {
  primary?: boolean | null;
  sources?: Provider[] | null;
  source?: Provider | null;
}) => {
  if (primary === true) {
    return true;
  }

  if (sources && sources.includes(Provider.Primary)) {
    return true;
  }

  if (source && source === Provider.Primary) {
    return true;
  }

  return false;
};
